import { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import {
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  AlertCircle,
  Wrench,
  CalendarClock,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { useFeatureFlags, type FeatureKey } from '@/contexts/FeatureFlagContext';

export default function FeatureFlagsPage() {
  const { flags, isLoading, toggleFlag, refreshFlags, maintenance, updateMaintenance } =
    useFeatureFlags();
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  const handleToggle = async (key: FeatureKey, currentEnabled: boolean) => {
    setTogglingKey(key);
    setError(null);
    try {
      await toggleFlag(key, !currentEnabled);
    } catch {
      setError(`Failed to toggle "${key}"`);
    } finally {
      setTogglingKey(null);
    }
  };

  const enabledCount = flags.filter((f) => f.enabled).length;
  const disabledCount = flags.filter((f) => !f.enabled).length;

  const handleMaintenanceToggle = async () => {
    setMaintenanceLoading(true);
    setError(null);
    try {
      if (!maintenance.enabled) {
        // Activating maintenance
        await updateMaintenance({
          enabled: true,
          start_time: startTime ? new Date(startTime).toISOString() : new Date().toISOString(),
          end_time: endTime ? new Date(endTime).toISOString() : null,
          message: maintenanceMessage || null,
        });
      } else {
        // Deactivating maintenance
        await updateMaintenance({ enabled: false });
      }
    } catch {
      setError('Erreur lors de la mise à jour du mode maintenance');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  return (
    <AdminLayout
      title="Feature Flags"
      subtitle="Activer ou désactiver les fonctionnalités en temps réel"
      action={
        <button
          onClick={refreshFlags}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Rafraîchir
        </button>
      }
    >
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{flags.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">Activées</p>
          <p className="text-2xl font-bold text-[#16c47f]">{enabledCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">Désactivées</p>
          <p className="text-2xl font-bold text-red-500">{disabledCount}</p>
        </div>
      </div>

      {/* Maintenance Mode Section */}
      <div
        className={`mb-8 rounded-2xl border-2 transition-all duration-200 ${
          maintenance.enabled
            ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-800'
            : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/50'
        } p-6`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                maintenance.enabled
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : 'bg-gray-100 dark:bg-gray-700'
              }`}
            >
              <Wrench
                className={`w-5 h-5 ${maintenance.enabled ? 'text-red-500' : 'text-gray-400'}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Mode Maintenance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {maintenance.enabled
                  ? 'La maintenance est active — les utilisateurs voient la page de maintenance'
                  : 'Activez pour afficher une page de maintenance à tous les utilisateurs'}
              </p>
            </div>
          </div>
          {maintenance.enabled && (
            <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/30 px-3 py-1.5 rounded-full">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-semibold text-red-600 dark:text-red-400">EN COURS</span>
            </div>
          )}
        </div>

        {!maintenance.enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <CalendarClock className="w-4 h-4" />
                Début de maintenance
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#16c47f] focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Clock className="w-4 h-4" />
                Fin estimée
              </label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#16c47f] focus:border-transparent outline-none transition-all"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                Message personnalisé (optionnel)
              </label>
              <input
                type="text"
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="Nous effectuons des améliorations pour vous..."
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#16c47f] focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>
        )}

        {maintenance.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-sm">
            {maintenance.start_time && (
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl px-4 py-3">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Début</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(maintenance.start_time).toLocaleString('fr-FR')}
                </p>
              </div>
            )}
            {maintenance.end_time && (
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl px-4 py-3">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Fin estimée</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(maintenance.end_time).toLocaleString('fr-FR')}
                </p>
              </div>
            )}
            {maintenance.message && (
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl px-4 py-3">
                <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Message</p>
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {maintenance.message}
                </p>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleMaintenanceToggle}
          disabled={maintenanceLoading}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
            maintenance.enabled
              ? 'bg-[#16c47f] hover:bg-[#13a96c] text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          } ${maintenanceLoading ? 'opacity-50 cursor-wait' : ''}`}
        >
          <Wrench className="w-4 h-4" />
          {maintenanceLoading
            ? 'Mise à jour...'
            : maintenance.enabled
              ? 'Désactiver la maintenance'
              : 'Activer la maintenance'}
        </button>
      </div>

      {/* Section title for feature flags */}
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Fonctionnalités</h2>

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-4 w-64 bg-gray-100 dark:bg-gray-700/50 rounded" />
                </div>
                <div className="h-8 w-14 bg-gray-200 dark:bg-gray-700 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => {
            const isToggling = togglingKey === flag.key;
            return (
              <div
                key={flag.id}
                className={`bg-white dark:bg-gray-800 rounded-2xl border transition-all duration-200 ${
                  flag.enabled
                    ? 'border-[#16c47f]/30 dark:border-[#16c47f]/20'
                    : 'border-gray-100 dark:border-gray-700/50 opacity-75'
                } p-5`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {flag.label}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          flag.enabled
                            ? 'bg-[#16c47f]/10 text-[#16c47f]'
                            : 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                        }`}
                      >
                        {flag.enabled ? 'Activé' : 'Désactivé'}
                      </span>
                    </div>
                    {flag.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{flag.description}</p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                      Clé:{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">
                        {flag.key}
                      </code>
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle(flag.key as FeatureKey, flag.enabled)}
                    disabled={isToggling}
                    className={`flex-shrink-0 ml-4 p-1 rounded-full transition-all duration-200 ${
                      isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:scale-110'
                    }`}
                    title={flag.enabled ? 'Désactiver' : 'Activer'}
                  >
                    {flag.enabled ? (
                      <ToggleRight className="w-10 h-10 text-[#16c47f]" strokeWidth={1.5} />
                    ) : (
                      <ToggleLeft
                        className="w-10 h-10 text-gray-300 dark:text-gray-600"
                        strokeWidth={1.5}
                      />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
